import { useLocation } from "@Light/components/page/account";
import { useLight } from "@Light/services/light";
import type { PlanResponse } from "@Light/services/lightTypes";
import { LocationPlan, RateBase, RateGroup } from "@Light/services/lightTypes";
import { createContext, useContext, useState } from "react";
import { useScaffold } from "./context";
import { PlanFeatures, planFeaturesForRateGroup } from "./planFeatures";
import { useEnrollmentRateGroups, useRateGroup } from "./rateGroup";

export type Plan = {
  group: RateGroup;
  requestedGroup?: RateGroup;
  plan?: RateBase | LocationPlan;
  features: PlanFeatures;
  /**
   * Replaces which `group` is fetched and provided. Only provided in
   * enrollment contexts.
   */
  switchGroup?: (groupUUID: string) => void;
};

export const PlanContext = createContext<Plan | undefined>(undefined);

export type WithLocationPlanProps = {
  children?: React.ReactNode;
};

export const WithLocationPlan: React.FC<WithLocationPlanProps> = ({
  children,
}) => {
  const scaffold = useScaffold();
  const location = useLocation();
  const { useGetLocationPlanQuery } = useLight();
  const rateQuery = useGetLocationPlanQuery({ uuid: location.uuid });
  const group = useRateGroup(rateQuery.data?.group_uuid);

  if (rateQuery.isError) {
    const Field = scaffold.system.field;
    const ErrorMessage = scaffold.system.errorMessage;
    return (
      <Field>
        <ErrorMessage>
          Failed querying rate data. Please try again later
        </ErrorMessage>
      </Field>
    );
  }

  if (!rateQuery.isSuccess || !group) {
    return null;
  }

  const plan = rateQuery.data;

  const features = planFeaturesForRateGroup(plan || group);

  return (
    <PlanContext.Provider
      value={{ group, requestedGroup: undefined, plan, features }}
    >
      {children}
    </PlanContext.Provider>
  );
};

export type WithPlanProps = {
  planUUID?: string;
  children?: React.ReactNode;
};

export const WithPlan: React.FC<WithPlanProps> = ({ planUUID, children }) => {
  const [switchedPlanUUID, setSwitchedPlanUUID] = useState<string>();
  const groups = useEnrollmentRateGroups(switchedPlanUUID ?? planUUID);

  if (!groups) {
    return null;
  }

  const { requestedGroup, effectiveGroup } = groups;
  const features = planFeaturesForRateGroup(effectiveGroup);

  return (
    <PlanContext.Provider
      value={{
        group: effectiveGroup,
        requestedGroup,
        features,
        switchGroup: setSwitchedPlanUUID,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
};

export function usePlan(): Plan {
  const plan = useContext(PlanContext);
  if (!plan) {
    throw new Error("could not find plan when calling usePlan");
  }

  return plan;
}

export function useOptionalPlan(): Plan | undefined {
  return useContext(PlanContext);
}

export const RateContext = createContext<PlanResponse | undefined>(undefined);

export function useRate(): PlanResponse {
  const rate = useContext(RateContext);
  if (!rate) {
    throw new Error("could not find rate when calling useRate");
  }

  return rate;
}

export function useOptionalRate(): PlanResponse | undefined {
  return useContext(RateContext);
}
